import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getCustomerNotes, addCustomerNote } from "@/lib/demo-data/customer-notes";
import type { CustomerNote, CustomerNotesApiResponse, CreateNoteRequest } from "@/types/customer-note";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Hook to fetch customer notes
 * Handles both Demo and Live modes
 */
export function useCustomerNotes(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = customerId ? getCustomerNotes(customerId) : [];

  return useModeAwareQuery<CustomerNote[], Error>(
    ["customer-notes", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CustomerNotesApiResponse>(
        `/api/v1/customers/${customerId}/notes`,
        undefined,
        signal
      );
      return response.notes || [];
    },
    mockNotes,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to add a new customer note
 * Handles both Demo and Live modes
 */
export function useAddCustomerNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateNoteRequest & { userId: string; userName: string }) => {
      // In demo mode, use the mock function
      // In live mode, call the API
      try {
        const response = await apiPost<CustomerNote>(
          `/api/v1/customers/${request.customerId}/notes`,
          {
            content: request.content,
          }
        );
        return response;
                        } catch {
                    // Fallback to mock data if API fails or in demo mode
        return addCustomerNote(
          request.customerId,
          request.content,
          request.userId,
          request.userName
        );
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch notes for this customer
      queryClient.invalidateQueries({ queryKey: ["customer-notes", variables.customerId] });
    },
  });
}
